import { ChevronDown, ChevronUp, PiggyBank } from "lucide-react";
import { useId, useState } from "react";

import {
  formatCompactCurrency,
  type DashboardSnapshot,
} from "../../../../lib/dashboard";
import {
  useTargetEdit,
  type TargetEditPatch,
  type UseTargetEditOptions,
} from "../../../../lib/profile/use-target-edit";
import type { ReadinessTarget } from "../../../../lib/readiness-types";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../ui/input";

interface DownPaymentPlanRowProps {
  snapshot: DashboardSnapshot;
  target: ReadinessTarget | null | undefined;
  opts: UseTargetEditOptions;
}

// Writes savings_goal_pct AND a derived savings_goal_amount in the same
// PUT body. The backend reconciles the pair too; writing both atomically
// from every call site guards against future regressions.
export function DownPaymentPlanRow({
  snapshot,
  target,
  opts,
}: DownPaymentPlanRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const editTarget = useTargetEdit(opts);
  const editorId = useId();

  // target_price is in cents (Money = number); convert to dollars.
  const targetPriceDollars =
    target?.deal.property.target_price !== undefined &&
    target.deal.property.target_price !== null
      ? Math.round(target.deal.property.target_price / 100)
      : snapshot.targetPrice;

  const currentPct = target?.deal.savings_goal_pct ?? null;
  const currentPctDisplay =
    currentPct !== null ? Math.round(currentPct * 100) : null;
  const currentAmount =
    target?.deal.savings_goal_amount ??
    (currentPct !== null && targetPriceDollars
      ? Math.round(targetPriceDollars * currentPct)
      : null);

  const headlineValue =
    currentPctDisplay !== null
      ? `${currentPctDisplay}%${
          currentAmount ? ` (~${formatCompactCurrency(currentAmount)})` : ""
        }`
      : "Not set";

  const initialDraft =
    currentPctDisplay !== null ? String(currentPctDisplay) : "";

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value.trim();
    setValidationError(null);
    if (raw === initialDraft) {
      return;
    }
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      setValidationError("Enter a percentage between 0 and 100.");
      return;
    }
    const pct = parsed / 100;
    const patch: TargetEditPatch = { savings_goal_pct: pct };
    if (targetPriceDollars > 0) {
      patch.savings_goal_amount = Math.round(targetPriceDollars * pct);
    }
    editTarget.mutate(patch).catch(() => {});
  };

  const error = editTarget.error
    ? "Couldn't save — please try again."
    : validationError;

  return (
    <article
      id="profile-row-down_payment_plan"
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "overflow-hidden p-0 transition-colors hover:bg-accent/35",
        expanded && "bg-background/60",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
        aria-label="Down-payment plan"
        aria-expanded={expanded}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
            <PiggyBank aria-hidden className="size-4" strokeWidth={2} />
          </span>
          <span className="block min-w-0 truncate text-base font-semibold text-foreground">
            Down-payment plan
          </span>
        </span>
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
          <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
            {headlineValue}
          </span>
          {expanded ? (
            <ChevronUp aria-hidden className="h-4 w-4" />
          ) : (
            <ChevronDown aria-hidden className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded ? (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="max-w-xl">
            <Input
              key={initialDraft}
              id={editorId}
              type="number"
              inputMode="numeric"
              className="font-bold tabular-nums"
              defaultValue={initialDraft}
              aria-label="Down-payment plan"
              disabled={editTarget.isPending}
              error={error ?? undefined}
              endAdornment="%"
              label="Down-payment plan"
              max={100}
              min={0}
              onBlur={handleBlur}
              step={0.5}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Sill writes both the % and the dollar equivalent in the same save.
            </p>
          </div>
        </div>
      ) : null}
    </article>
  );
}
